/**
 * LayerPanel — the layer stack with drag-to-reorder (@dnd-kit) and an "Add"
 * menu. The editor stores layers bottom→top (index 0 = bottom of the canvas),
 * but the list is shown top-first, so we render a reversed copy and translate
 * reversed indices back to real ones before calling actions.reorderLayers.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Eye, EyeOff, Lock, Unlock, Copy, Trash2, Plus,
  Image as ImageIcon, Square, Layers as LayersIcon, Type,
} from 'lucide-react';
import { T } from '../../../components/em/tokens';
import {
  LAYER_TYPE_LABEL, makeBackgroundLayer, makeForegroundLayer, makeNameLayer,
} from '../pgConstants';

const TYPE_ICON = {
  background: Square,
  image: ImageIcon,
  foreground: LayersIcon,
  name: Type,
};

const sectionTitle = {
  font: `600 11px/1 ${T.font}`,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: T.fg7,
};

// Tiny inline icon button used inside a row (visibility / lock / dup / delete).
function RowBtn({ icon: Icon, title, onClick, danger, muted }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        width: 24, height: 24, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: 0, background: 'transparent', borderRadius: T.rSm, cursor: 'pointer',
        color: danger ? T.danger : muted ? T.fg7 : T.fg4,
      }}
    >
      <Icon size={14} />
    </button>
  );
}

function LayerRow({ layer, selected, actions }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: layer.id });

  const Icon = TYPE_ICON[layer.type] || Square;
  const label = layer.name || LAYER_TYPE_LABEL[layer.type] || layer.type;

  return (
    <div
      ref={setNodeRef}
      className="pg-layer-row"
      onClick={() => actions.selectLayer(layer.id)}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 6px',
        marginBottom: 4,
        borderRadius: T.rMd,
        border: `1px solid ${selected ? T.orange : 'transparent'}`,
        background: selected ? T.orange50 : isDragging ? T.bgMuted : 'transparent',
        cursor: 'pointer',
        opacity: isDragging ? 0.85 : layer.visible ? 1 : 0.55,
        boxShadow: isDragging ? T.shadowMd : 'none',
      }}
    >
      <span
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 24, flexShrink: 0,
          color: T.fg7, cursor: 'grab', touchAction: 'none',
        }}
      >
        <GripVertical size={14} />
      </span>

      <Icon size={14} style={{ color: selected ? T.orange : T.fg5, flexShrink: 0 }} />

      <span style={{
        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        font: `${selected ? 600 : 500} 12px/1.3 ${T.font}`,
        color: selected ? T.orange : T.fg2,
      }}>
        {label}
      </span>

      <RowBtn
        icon={layer.visible ? Eye : EyeOff}
        title={layer.visible ? 'Hide layer' : 'Show layer'}
        muted={!layer.visible}
        onClick={() => actions.toggleVisibility(layer.id)}
      />
      <RowBtn
        icon={layer.locked ? Lock : Unlock}
        title={layer.locked ? 'Unlock layer' : 'Lock layer'}
        muted={!layer.locked}
        onClick={() => actions.toggleLock(layer.id)}
      />
      <RowBtn icon={Copy} title="Duplicate layer" onClick={() => actions.duplicateLayer(layer.id)} />
      <RowBtn icon={Trash2} title="Delete layer" danger onClick={() => actions.deleteLayer(layer.id)} />
    </div>
  );
}

export default function LayerPanel({ state, actions, onAddImage }) {
  const { layers, selectedLayerId, canvasConfig } = state;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the Add menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // Top-first view of the bottom→top store.
  const reversed = [...layers].reverse();

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const vFrom = reversed.findIndex((l) => l.id === active.id);
    const vTo = reversed.findIndex((l) => l.id === over.id);
    if (vFrom === -1 || vTo === -1) return;
    // Translate reversed (visual) indices back into the real bottom→top array.
    const n = layers.length;
    const realFrom = n - 1 - vFrom;
    const realTo = n - 1 - vTo;
    actions.reorderLayers(realFrom, realTo);
  };

  const addItems = [
    { key: 'background', label: 'Background', icon: Square, run: () => actions.addLayer(makeBackgroundLayer(canvasConfig)) },
    { key: 'image', label: 'Image', icon: ImageIcon, run: () => onAddImage() },
    { key: 'foreground', label: 'Foreground', icon: LayersIcon, run: () => actions.addLayer(makeForegroundLayer(canvasConfig)) },
    { key: 'name', label: 'Name', icon: Type, run: () => actions.addLayer(makeNameLayer(canvasConfig)) },
  ];

  return (
    <div
      className="pg-layer-panel"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 12px 10px',
      }}>
        <span style={sectionTitle}>Layers</span>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="pg-add-layer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: T.rMd,
              border: `1px solid ${T.border}`, background: 'transparent',
              color: T.fg2, cursor: 'pointer', font: `600 12px/1 ${T.font}`,
            }}
          >
            <Plus size={14} />Add
          </button>

          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50,
                minWidth: 160, padding: 6,
                background: T.bgCard, border: `1px solid ${T.border}`,
                borderRadius: T.rLg, boxShadow: T.shadowMd,
              }}
            >
              {addItems.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  role="menuitem"
                  onClick={() => { it.run(); setMenuOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '8px 10px', border: 0, background: 'transparent',
                    borderRadius: T.rSm, cursor: 'pointer', textAlign: 'left',
                    color: T.fg2, font: `500 13px/1 ${T.font}`,
                  }}
                >
                  <it.icon size={15} style={{ color: T.fg5 }} />{it.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 12px' }}>
        {reversed.length === 0 ? (
          <div style={{ padding: '24px 12px', textAlign: 'center', color: T.fg7, font: `400 12px/1.5 ${T.font}` }}>
            No layers yet. Use “Add” to start.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={reversed.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {reversed.map((layer) => (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  selected={layer.id === selectedLayerId}
                  actions={actions}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
